import { createFileRoute, Link, useNavigate, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useRef, useState } from "react";
import { BarChart3, FolderPlus, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button, PageContent, PageHeader, PageShell, type DataTableState } from "@h2j-data-team/ui";
import { toast } from "@/components/shared";
import { BackLink } from "@/components/back-link";
import { AddAppsToGroupDrawer } from "@/features/groups/components/add-apps-to-group-drawer";
import { useCreateJob } from "@/features/jobs/api";
import { AppTable } from "@/components/app/app-table";
import type { AppFilters } from "@/components/app/app-filters";
import { useSearchHotkey } from "@/lib/use-search-hotkey";
import {
  connectionQueryOptions,
  connectionAppsQueryOptions,
  useRefreshAppMetadata,
} from "@/features/qlik-connections/api";

export const Route = createFileRoute("/_authenticated/single-app/$connectionId/apps")({
  staticData: { pageTitle: "breadcrumbs.selectApp", breadcrumb: "breadcrumbs.selectApp" },
  validateSearch: (search: Record<string, unknown>) => ({
    q: typeof search.q === "string" ? search.q : undefined,
    spaces: typeof search.spaces === "string" ? search.spaces : undefined,
    owners: typeof search.owners === "string" ? search.owners : undefined,
    status: typeof search.status === "string" ? search.status : undefined,
    access: typeof search.access === "string" ? search.access : undefined,
  }),
  component: SingleAppAppsPage,
});

const FILTER_KEYS = ["spaces", "owners", "status", "access"] as const;

function csvToList(csv: string | undefined): string[] {
  if (!csv) return [];
  return csv
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);
}

function listToCsv(list: string[]): string | undefined {
  return list.length === 0 ? undefined : list.join(",");
}

function SingleAppAppsPage() {
  const { t } = useTranslation("single-app");
  const { connectionId } = useParams({ from: "/_authenticated/single-app/$connectionId/apps" });
  const search = Route.useSearch();
  const { q } = search;
  const navigate = useNavigate({ from: Route.fullPath });
  const searchRef = useRef<HTMLInputElement>(null);
  useSearchHotkey(searchRef);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [recaching, setRecaching] = useState(false);
  const createJob = useCreateJob();
  const refreshMeta = useRefreshAppMetadata();

  // The search and the filters live in the URL (shareable, survive a reload);
  // page, size, sort and the selection are the table's own.
  const [local, setLocal] = useState<DataTableState<AppFilters>>({
    page: 1,
    pageSize: 50,
    query: "",
    filters: { spaces: [], owners: [], status: [], access: [] },
    selection: [],
  });
  const filters = useMemo<AppFilters>(
    () => ({
      spaces: csvToList(search.spaces),
      owners: csvToList(search.owners),
      status: csvToList(search.status),
      access: csvToList(search.access),
    }),
    [search.spaces, search.owners, search.status, search.access],
  );
  const state: DataTableState<AppFilters> = { ...local, query: q ?? "", filters };

  const onStateChange = useCallback(
    (next: DataTableState<AppFilters>) => {
      const searchChanged =
        next.query !== (q ?? "") ||
        FILTER_KEYS.some((key) => listToCsv(next.filters[key]) !== listToCsv(filters[key]));
      // The table clears the selection when the search or a filter changes. Here
      // the selection is a basket built across searches (search A, tick, search B,
      // tick, add to a group), as it always was: keep it.
      setLocal(searchChanged ? { ...next, selection: local.selection } : next);
      const patch: Record<string, string | undefined> = {};
      if (next.query !== (q ?? "")) patch.q = next.query || undefined;
      for (const key of FILTER_KEYS) {
        if (listToCsv(next.filters[key]) !== listToCsv(filters[key])) {
          patch[key] = listToCsv(next.filters[key]);
        }
      }
      if (Object.keys(patch).length > 0) {
        navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
      }
    },
    [filters, local.selection, navigate, q],
  );

  const connectionQuery = useQuery(connectionQueryOptions(connectionId));
  const connection = connectionQuery.data;

  const appsQuery = useQuery({
    ...connectionAppsQueryOptions(connectionId),
    enabled: !!connectionId,
  });
  const { data: apps, refetch: refetchApps, isFetching: isFetchingApps } = appsQuery;

  const clearSelection = useCallback(() => setLocal((prev) => ({ ...prev, selection: [] })), []);
  const selectedApps = (apps ?? []).filter((a) => local.selection.includes(a.id));

  const handleBulkAnalyze = async () => {
    if (selectedApps.length === 0) return;
    setAnalyzing(true);
    const results = await Promise.allSettled(
      selectedApps.map((a) =>
        createJob.mutateAsync({
          type: "analyze_app",
          payload: {
            connectionId,
            appId: a.id,
            appName: a.name,
            spaceName: a.spaceName ?? undefined,
          },
        }),
      ),
    );
    setAnalyzing(false);
    const started = results.filter((r) => r.status === "fulfilled").length;
    const failed = results.length - started;
    if (started > 0) {
      toast.success(t("apps.bulk.analyzeStarted", { count: started }));
      clearSelection();
      navigate({
        to: "/jobs",
        search: {
          type: undefined,
          status: undefined,
          q: undefined,
          dateFrom: undefined,
          dateTo: undefined,
        },
      });
    }
    if (failed > 0) {
      toast.error(t("apps.bulk.analyzeFailed", { count: failed }));
    }
  };

  const handleBulkRecache = async () => {
    if (selectedApps.length === 0) return;
    setRecaching(true);
    let ok = 0;
    let failed = 0;
    const CONCURRENCY = 4;
    for (let i = 0; i < selectedApps.length; i += CONCURRENCY) {
      const batch = selectedApps.slice(i, i + CONCURRENCY);
      const results = await Promise.allSettled(
        batch.map((a) => refreshMeta.mutateAsync({ connectionId, appId: a.id })),
      );
      ok += results.filter((r) => r.status === "fulfilled").length;
      failed += results.filter((r) => r.status === "rejected").length;
    }
    setRecaching(false);
    if (ok > 0) toast.success(t("apps.bulk.recached", { count: ok }));
    if (failed > 0) toast.error(t("apps.bulk.recacheFailed", { count: failed }));
    clearSelection();
  };

  const failed = connectionQuery.isError || appsQuery.isError;
  const retry = () => {
    if (connectionQuery.isError) void connectionQuery.refetch();
    if (appsQuery.isError) void refetchApps();
  };

  return (
    <PageShell
      header={
        <PageHeader
          title={connection?.name || t("apps.pageTitle")}
          description={
            failed
              ? t("apps.errorDescription")
              : apps
                ? t("apps.description", { count: apps.length })
                : t("apps.loadingDescription")
          }
          back={<BackLink to="/single-app" label={t("apps.backToConnections")} />}
          actions={
            <Button
              variant="outline"
              size="sm"
              loading={isFetchingApps}
              onClick={() => void refetchApps()}
            >
              <RefreshCw />
              {t("apps.refresh")}
            </Button>
          }
        />
      }
    >
      <PageContent fill>
        <AppTable
          label={t("apps.pageTitle")}
          apps={apps ?? []}
          connectionId={connectionId}
          state={state}
          onStateChange={onStateChange}
          loading={connectionQuery.isPending || appsQuery.isPending}
          error={failed ? { title: t("apps.loadFailed") } : null}
          onRetry={retry}
          searchRef={searchRef}
          showDates
          emptyAction={
            <Button asChild variant="outline">
              <Link to="/single-app" search={{ q: undefined }}>
                {t("apps.backToConnections")}
              </Link>
            </Button>
          }
          bulkActions={() => (
            <>
              <Button size="sm" onClick={() => setPickerOpen(true)}>
                <FolderPlus />
                {t("apps.bulk.addToGroup")}
              </Button>
              <Button
                size="sm"
                variant="outline"
                loading={analyzing}
                onClick={() => void handleBulkAnalyze()}
              >
                <BarChart3 />
                {t("apps.bulk.analyze")}
              </Button>
              <Button
                size="sm"
                variant="outline"
                loading={recaching}
                onClick={() => void handleBulkRecache()}
              >
                <RefreshCw />
                {t("apps.bulk.recache")}
              </Button>
            </>
          )}
        />
      </PageContent>

      <AddAppsToGroupDrawer
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        connectionId={connectionId}
        apps={selectedApps}
        onAdded={clearSelection}
      />
    </PageShell>
  );
}
